import { useState } from 'react';
import type { FormEvent } from 'react';
import type { ChatMessage, Conversation } from '@lemontaigu/chat';
import { getReceptionStore } from './data/store.js';
import { dayRangeToIso, groupHistoryByDate, sortHistoryByDate } from './logic.js';
import { LANG_LABELS, MessageList } from './messageDisplay.js';
import { FrDateInput } from './ui/FrDateInput.js';

// History browse (as opposed to the live dashboard's capped recent-activity
// view): search every conversation — open or closed — in a date range,
// optionally scoped to one room. Two presentations of the SAME query
// (searchConversations), chosen by whether a room filter is set:
//  * room set    -> flat list, most recent first ("this room, this period");
//  * room empty  -> grouped by day, rooms ascending within a day
//                   ("this period, which rooms").
// Read-only by design — postStaffMessage already allows replying into a
// closed thread from the live dashboard if a genuine follow-up is needed.

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function History() {
  const store = getReceptionStore();
  const [dateFrom, setDateFrom] = useState(todayIso());
  const [dateTo, setDateTo] = useState(todayIso());
  const [room, setRoom] = useState('');
  const [results, setResults] = useState<Conversation[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  async function search(e: FormEvent): Promise<void> {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSelectedId(null);
    setMessages([]);
    try {
      const { fromIso, toIso } = dayRangeToIso(dateFrom, dateTo);
      setResults(await store.searchConversations(fromIso, toIso, room.trim() || undefined));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'recherche impossible');
      setResults(null);
    } finally {
      setLoading(false);
    }
  }

  async function select(conversation: Conversation): Promise<void> {
    setSelectedId(conversation.id);
    setMessages(await store.listMessages(conversation.id));
  }

  const roomFiltered = room.trim() !== '';
  const flat = results && roomFiltered ? sortHistoryByDate(results) : null;
  const grouped = results && !roomFiltered ? groupHistoryByDate(results) : null;

  const row = (c: Conversation, label: string) => (
    <li key={c.id}>
      <button
        className={['history-item', c.id === selectedId ? 'is-selected' : ''].join(' ')}
        onClick={() => void select(c)}
      >
        <span>{label}</span>
        <span className="thread-lang">{LANG_LABELS[c.guestLang]}</span>
        {c.status === 'closed' && <span className="history-closed">clos</span>}
      </button>
    </li>
  );

  return (
    <div className="history">
      <form className="history-filters" onSubmit={(e) => void search(e)}>
        <label>
          Du
          <FrDateInput value={dateFrom} onChange={setDateFrom} />
        </label>
        <label>
          Au
          <FrDateInput value={dateTo} onChange={setDateTo} />
        </label>
        <label>
          Chambre
          <input
            type="text"
            value={room}
            onChange={(e) => setRoom(e.target.value)}
            placeholder="Toutes"
            maxLength={8}
          />
        </label>
        <button type="submit" disabled={loading}>
          Rechercher
        </button>
      </form>

      {error && <p className="history-error">{error}</p>}

      <div className="history-body">
        <div className="history-results">
          {results === null && !loading && (
            <p className="muted">Choisissez une période puis lancez la recherche.</p>
          )}
          {results !== null && results.length === 0 && (
            <p className="muted">Aucune conversation pour cette période.</p>
          )}

          {flat && <ul>{flat.map((c) => row(c, `${c.createdAt.slice(0, 10)} — Ch. ${c.room}`))}</ul>}

          {grouped?.map((group) => (
            <div key={group.date} className="history-date-group">
              <h3>{group.date}</h3>
              <ul>{group.conversations.map((c) => row(c, `Ch. ${c.room}`))}</ul>
            </div>
          ))}
        </div>

        <div className="history-thread">
          {selectedId ? <MessageList messages={messages} /> : <p className="muted">Sélectionnez une conversation.</p>}
        </div>
      </div>
    </div>
  );
}
